import React, { useEffect, useState, useRef } from "react";
import authStorage from "../utils/authStorage";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Editor } from "@tinymce/tinymce-react";
import { BASE_URL } from "../config";
const ForumEdit = ({ forumData, goBack, getHealthyContentList, onSaved }) => {
  useEffect(() => {
    let isAdmin = ["admin"].includes(authStorage.getItem("roles"));

    // If roles is not admin, redirect to dashboard page
    if (!isAdmin) {
      window.location = window.location.origin + "/membership";
      return false;
    }
  });

  const [formData, setFormData] = useState({
    id: forumData._id,
    title: forumData.title,
    content: forumData.content,
    threadBy: forumData.threadBy,
  });
  const [showForumDetail, setShowForumDetail] = useState("");
  const [showError, setShowError] = useState(false);

  const [formClicked, setFormClicked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  /*
   * Per-field, so each message can sit under the field it refers to rather than
   * as one combined sentence at the bottom of the form. Only after a submit
   * attempt -- flagging empty fields before anyone has tried is just nagging.
   */
  const titleInvalid = formClicked && !formData?.title;
  const contentInvalid = formClicked && !formData?.content;

  /**
   * The invalid state plus the aria wiring a flagged field needs.
   *
   * The red ring is a BOX-SHADOW, not a border: `input, select` in src/index.css
   * sets `border` with `!important`, which beats even an inline style, so a red
   * border simply never appeared. box-shadow is untouched by that rule.
   */
  const fieldState = (invalid, id) => ({
    style: {
      background: '#fff',
      border: '1px solid #E5E7EB',
      borderRadius: '10px',
      boxShadow: invalid ? '0 0 0 1.5px #E53E14' : undefined,
    },
    'aria-invalid': invalid || undefined,
    'aria-describedby': invalid ? `${id}-error` : undefined,
  });

  const editorRef = useRef(null);
  const getText = () => {
    if (editorRef.current) {
      console.log(editorRef.current.getContent());
      setFormData({ ...formData, content: editorRef.current.getContent() });
    }
  };

  const [inputValue, setInputValue] = useState(forumData.title);

  const handleChange = (e) => {
    setInputValue(e.target.value); // Update the inputValue state with the new value
    setFormData({ ...formData, title: e.target.value });
  };

  const navigate = useNavigate();

  useEffect(() => {
    if (!formClicked) {
      return;
    }
    if (!formData?.title || !formData?.content) {
      setShowError(true);
      return;
    } else {
      setShowError(false);
    }
  }, [formData]);

  const updateForumPost = () => {
    setLoading(true);
    setFormClicked(true);

    /*
     * Read the editor at submit time rather than trusting `formData.content`,
     * which is only refreshed by `getText()` on the editor's mouseleave. Submit
     * via keyboard, or any path that does not cross the editor boundary, left a
     * stale value and the form rejected content that was plainly on screen.
     */
    const liveContent = editorRef.current ? editorRef.current.getContent() : formData?.content;
    const next = { ...formData, content: liveContent };
    if (liveContent !== formData?.content) setFormData(next);

    if (!next?.title || !next?.content) {
      setShowError(true);
      setLoading(false);
      return;
    }
    axios({
      headers: {
        "x-access-token": authStorage.getItem("auth-token"),
      },
      url: `${BASE_URL}/forum`, //your url
      method: "put",
      data: next,
    })
      .then(() => {
        setLoading(false);
        // This form is rendered INSIDE the listing, so it reports the outcome
        // upward and the listing shows it once this closes.
        if (onSaved) onSaved('Forum thread updated.');
        goBack();
        getHealthyContentList();
      })
      .catch((err) => {
        setLoading(false);
        setSubmitError(
          err?.response?.data?.message || 'Could not save your changes. Please try again.'
        );
      });
  };

  return (
    <div className="mx-auto my-6 w-full max-w-4xl border border-solid border-gray-200 bg-white p-6 md:p-8" style={{ borderRadius: '14px' }}>
      <div className="mb-6 flex items-center gap-3 border-0 border-b border-solid border-gray-100 pb-5">
        <button
          onClick={() => goBack()}
          type="button"
          aria-label="Back"
          className="flex cursor-pointer items-center justify-center border border-solid border-gray-200 bg-white p-2 text-gray-600 appearance-none transition-colors hover:bg-gray-50"
          style={{ borderRadius: '10px' }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
            <path fillRule="evenodd" d="M18 10a.75.75 0 01-.75.75H4.66l2.1 1.95a.75.75 0 11-1.02 1.1l-3.5-3.25a.75.75 0 010-1.1l3.5-3.25a.75.75 0 111.02 1.1l-2.1 1.95h12.59A.75.75 0 0118 10z" clipRule="evenodd" />
          </svg>
        </button>
        <h2 className="text-lg font-semibold text-gray-900">Edit Post</h2>
      </div>
      {submitError && (
        <div
          role="alert"
          className="mb-5 flex items-start gap-3 border border-solid p-3.5 text-sm"
          style={{ borderRadius: '10px', background: '#FFF5F3', borderColor: '#F3C2B6', color: '#8C2409' }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true">
            <path fillRule="evenodd" d="M18 10A8 8 0 1 1 2 10a8 8 0 0 1 16 0Zm-7-4a1 1 0 1 0-2 0v4a1 1 0 1 0 2 0V6Zm-1 8a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd" />
          </svg>
          <span>{submitError}</span>
        </div>
      )}
      <div className="mb-4 mt-6">
        <div className="pb-1.5 text-left text-sm font-medium text-gray-700">Subject</div>
        <input
          value={inputValue}
          onChange={handleChange}
          type="text"
          className="block w-full appearance-none px-3.5 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none"
          {...fieldState(titleInvalid, 'subject')}
          placeholder="Give the thread a subject"
          required
        />
                {titleInvalid && (
                  <p id="subject-error" className="mt-1.5 flex items-center gap-1.5 text-[13px]" style={{ color: '#E53E14' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 shrink-0" aria-hidden="true">
                      <path fillRule="evenodd" d="M18 10A8 8 0 1 1 2 10a8 8 0 0 1 16 0Zm-7-4a1 1 0 1 0-2 0v4a1 1 0 1 0 2 0V6Zm-1 8a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd" />
                    </svg>
                    Subject is required.
                  </p>
                )}
      </div>

      <div className="pb-1.5 text-left text-sm font-medium text-gray-700">Content</div>
      <Editor
        className=" rounded-none	"
        onMouseLeave={() => getText()}
        apiKey="g64cjnqhg5z2xyt5dqqnvtvugubbr19suf3mljwa9h2ys1sr"
        onInit={(evt, editor) => (editorRef.current = editor)}
        initialValue={forumData.content || ""}
        init={{
          height: 300,
          menubar: true,
          plugins: [
            "advlist",
            "autolink",
            "lists",
            "link",
            "image",
            "charmap",
            "preview",
            "anchor",
            "searchreplace",
            "visualblocks",
            "code",
            "fullscreen",
            "insertdatetime",
            "media",
            "table",
            "code",
            "help",
            "wordcount",
            "fontsize", // Add 'fontsize' plugin
          ],
          toolbar:
            "undo redo | blocks | " +
            "bold italic forecolor image | alignleft aligncenter " +
            "alignright alignjustify | bullist numlist outdent indent | " +
            "removeformat | fontsizeselect | help" +
            "rounded-none",
          header_style:
            "body { font-size:14px; background-color: #fff; color: rgba(0, 0, 0, 0.5); border-radius: 0; }",
          content_style:
            "body { font-size:14px; background-color: #fff; color: rgba(0, 0, 0, 0.5); border-radius: 0; }",
        }}
      />

      {/* <textarea onChange={(e) => setFormData({ ...formData, content: e.target.value })} id="message" rows="4" className="block p-2.5 w-full text-sm text-gray-400 bg-slate-900 rounded-lg border border-gray-200 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500" placeholder="message" required></textarea> */}
                {contentInvalid && (
                  <p id="content-error" className="mt-1.5 flex items-center gap-1.5 text-[13px]" style={{ color: '#E53E14' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 shrink-0" aria-hidden="true">
                      <path fillRule="evenodd" d="M18 10A8 8 0 1 1 2 10a8 8 0 0 1 16 0Zm-7-4a1 1 0 1 0-2 0v4a1 1 0 1 0 2 0V6Zm-1 8a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd" />
                    </svg>
                    Content cannot be empty.
                  </p>
                )}
      <div className="mt-8 flex flex-wrap justify-end gap-3 border-0 border-t border-solid border-gray-100 pt-5">
        <button
          onClick={() => goBack()}
          type="button"
          className="cursor-pointer border border-solid border-gray-300 bg-white px-6 py-2.5 text-sm font-medium text-gray-700 appearance-none transition-colors hover:bg-gray-50"
          style={{ borderRadius: '10px' }}
        >
          Cancel
        </button>
        <button
          onClick={() => updateForumPost()}
          type="button"
          className="inline-flex cursor-pointer items-center gap-2 border-0 px-6 py-2.5 text-sm font-semibold text-white appearance-none transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          style={{ background: '#E53E14', borderRadius: '10px', boxShadow: '0 1px 2px rgba(229,62,20,0.35)' }}
        >
          {loading && (
            <svg
              aria-hidden="true"
              role="status"
              className="inline w-4 h-4 mr-3 text-white animate-spin"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="#E5E7EB"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentColor"
              />
            </svg>
          )}
          Update
        </button>
      </div>
    </div>
  );
};
export default ForumEdit;
